import { KeyValue } from '@/components/inspect/KeyValue';
import { EmptyState, ErrorPage, LoadingPage } from '@/components/primitives';
import { useRedisValue } from '@/hooks/useSturmData';
import { bytes, count, text } from '@/lib/inspectFormat';
import type { RedisEntry } from '@/types/inspect';

export function ValuePreview({ redisKey, database }: { redisKey: string | null; database: number }) {
  const { data, isLoading, isError, error } = useRedisValue(
    redisKey ?? '',
    database,
    redisKey != null,
  );

  if (redisKey == null) {
    return (
      <div className="card">
        <EmptyState title="No key selected" sub="Pick a key on the left to preview its value." />
      </div>
    );
  }

  if (isLoading) {
    return <LoadingPage label="Reading value…" />;
  }

  if (isError) {
    return (
      <ErrorPage
        title="Could not read the value"
        message={error instanceof Error ? error.message : 'Redis did not respond.'}
      />
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="card">
      <div className="card-hd">
        <div className="mono flex1" style={{ fontWeight: 600, wordBreak: 'break-all' }}>
          {data.key}
        </div>
        <span className="badge">{data.type}</span>
      </div>
      <div className="card-pad">
        <div className="kv mb-16">
          <KeyValue k="TTL" v={data.ttl == null ? 'no expiry' : data.ttl} />
          <KeyValue k="Length" v={count(data.length)} />
          <KeyValue k="Memory" v={data.memoryBytes == null ? '—' : bytes(data.memoryBytes)} />
        </div>

        {data.entries.length === 0 ? (
          <EmptyState title="Empty value" sub="The key exists but holds no entries." />
        ) : (
          <EntryList entries={data.entries} type={data.type} />
        )}

        {data.truncated ? (
          <div className="tiny muted" style={{ marginTop: 8 }}>
            Showing {count(data.entries.length)} of {count(data.length)} entries · values truncated.
          </div>
        ) : null}
      </div>
    </div>
  );
}

function EntryList({ entries, type }: { entries: RedisEntry[]; type: string }) {
  if (type === 'string') {
    return <div className="json">{entries[0]?.value ?? ''}</div>;
  }

  if (type === 'hash' || type === 'stream') {
    return (
      <div className="kv">
        {entries.map((entry, index) => (
          <KeyValue key={entry.field ?? index} k={text(entry.field)} v={entry.value} mono />
        ))}
      </div>
    );
  }

  if (type === 'zset') {
    return (
      <table className="tbl">
        <thead>
          <tr>
            <th>Member</th>
            <th>Score</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry, index) => (
            <tr key={`${entry.value}-${index}`}>
              <td className="mono">{entry.value}</td>
              <td className="tbl-num">{entry.score ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return <div className="json">{entries.map((entry) => entry.value).join('\n')}</div>;
}
